// The chat box's browser-run turn (claude-docs/2026-10-31-chat-app-actions-
// plan.md): held by ChatProvider, which sits above the router's pages, so a
// turn outlives the popup — an action that opens another page closes it.
// For whoever the logo menu gives the assistant, contractors and admins: it
// decides (`mayUseAssistant`), since this sits above the pages and knows
// nothing of the account. The only chat the web offers since 2026-11-04 (the
// plan's Step 4); the server-run turn is iOS voice's.
// Its engine (browserTurn.js: the page runner, the action catalog, the
// screenshot code) loads on the first send — ChatProvider is in every page's
// first download.
import { useCallback, useRef, useState } from 'react';
import { chatErrorMessage } from '../../services/chatErrorMessage';
import { keepChatForReload } from './chatAcrossReload';
import { historyFor, trimToBudget } from './chatTranscript';

/** Said after a reply whose action reloads the page. */
const RELOADING = 'The page is reloading to show the change.';

const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/** A turn's steps as a host shows them: each call's name, label and status. */
const stepsOf = (activity) =>
  activity
    .filter((a) => a.type === 'step')
    .map((a) => ({
      name: a.name,
      label: a.friendlyName ?? a.name,
      status: a.status,
    }));

/**
 * @param {{ messages, addMessage, setIsLoading, setProgress, conversationContext }} chat
 *   ChatProvider's own state
 * @param {object|null} chat.restoredConversation  the conversation a page
 *   reload kept (chatAcrossReload.js)
 */
export function useChatTurn({
  messages,
  addMessage,
  setIsLoading,
  setProgress,
  conversationContext,
  restoredConversation = null,
}) {
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [turnRunning, setTurnRunning] = useState(false);
  const running = useRef(false);
  const stopped = useRef(false);
  const answer = useRef(null);
  // The last turn's whole conversation and the chat message it ended on:
  // what the next turn carries (chatTranscript.historyFor).
  const conversation = useRef(restoredConversation);
  const latest = useRef({});
  latest.current = { messages, conversationContext };

  const answerConfirm = useCallback((allow) => {
    const resolve = answer.current;
    answer.current = null;
    setPendingConfirm(null);
    resolve?.(allow === true);
  }, []);

  const stopTurn = useCallback(() => {
    stopped.current = true;
    answerConfirm(false);
  }, [answerConfirm]);

  /**
   * @param {string} text
   * @param {{ voice?: boolean, onEvent?: (event: object) => void }} [options]
   *   `voice`: the turn is spoken, and the server asks for a short answer that
   *   reads aloud. `onEvent`: a host's view of the turn (iOS voice, through
   *   iosChatHost.js) — started, progress, confirm, reply or error, done.
   */
  const sendBrowserTurn = useCallback(
    async (text, { voice = false, onEvent = () => {} } = {}) => {
      if (running.current) {
        onEvent({
          type: 'error',
          message: 'The assistant is still answering the last request.',
        });
        return;
      }
      running.current = true;
      onEvent({ type: 'started' });
      stopped.current = false;
      setTurnRunning(true);
      const before = latest.current.messages;
      const chatContext = latest.current.conversationContext;
      const asked = {
        id: newId(),
        role: 'user',
        content: text,
        timestamp: new Date(),
      };
      addMessage(asked);
      setIsLoading(true);
      setProgress({ status: 'executing', message: 'Working…', activity: [] });
      const reply = (content, activity) => {
        const message = {
          id: newId(),
          role: 'assistant',
          content,
          activity: activity?.length ? activity : undefined,
          timestamp: new Date(),
        };
        addMessage(message);
        return message;
      };
      try {
        const { runBrowserTurn } = await import('./browserTurn');
        const out = await runBrowserTurn({
          text,
          history: trimToBudget(historyFor(conversation.current, before)),
          chatContext,
          readLocation: () => latest.current.conversationContext?.location,
          voice,
          confirm: (pending) =>
            new Promise((resolve) => {
              answer.current = resolve;
              setPendingConfirm(pending);
              onEvent({
                type: 'confirm',
                question: pending.description,
                call: pending.call.name,
              });
            }),
          onActivity: (activity) => {
            setProgress({ status: 'executing', message: 'Working…', activity });
            onEvent({ type: 'progress', steps: stepsOf(activity) });
          },
          isStopped: () => stopped.current,
        });
        const said = out.reply || (stopped.current ? 'Stopped.' : 'Done.');
        onEvent({ type: 'reply', text: said });
        const keep = (last) => {
          conversation.current = {
            transcript: out.transcript,
            coveredId: last.id,
          };
        };
        if (out.afterAnswer) {
          // The page goes away: keep the conversation for the next one.
          const last = reply(`${said}\n\n${RELOADING}`, out.activity);
          keep(last);
          keepChatForReload([...before, asked, last], conversation.current);
          out.afterAnswer();
        } else {
          keep(reply(said, out.activity));
        }
      } catch (error) {
        // What earlier rounds did has happened: keep it with the error. The
        // conversation stays as it was; the next turn carries this one's
        // request and error as lines of text.
        const partial = error?.partial;
        onEvent({ type: 'error', message: chatErrorMessage(error) });
        reply(
          [partial?.reply, chatErrorMessage(error)]
            .filter(Boolean)
            .join('\n\n'),
          partial?.activity
        );
      } finally {
        onEvent({ type: 'done' });
        running.current = false;
        answer.current = null;
        setPendingConfirm(null);
        setTurnRunning(false);
        setIsLoading(false);
        setProgress(null);
      }
    },
    [addMessage, setIsLoading, setProgress]
  );

  return {
    sendBrowserTurn,
    pendingConfirm,
    answerConfirm,
    stopTurn,
    turnRunning,
  };
}
